import { EXTERNAL_FUNCTIONS_URL, EXTERNAL_SUPABASE_ANON_KEY } from "@/config/supabase";
import type { BookingLinkEvent, BookingPageProposal } from "@/lib/reservations-booking-page";

export const RESERVATIONS_SETUP_ROUTE = "inicio/reservas";
export const RESERVATIONS_SETUP_PATH = `/app/${RESERVATIONS_SETUP_ROUTE}`;
export const SETUP_FUNCTION = "reservations-setup";
export const MAX_SETUP_ATTACHMENTS = 3;
export const SETUP_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
export const SHARE_LINK_REQUEST = "Quiero compartir mi enlace de reservas";
export const SETUP_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

export interface SetupTable {
  table_number: string;
  capacity: number;
}

export interface SetupZoneCard {
  name: string;
  existing: boolean;
  existing_tables: SetupTable[];
  new_tables: SetupTable[];
  can_combine: boolean | null;
  from_floor_plan: boolean;
}

export interface SetupProposal {
  type: "proposal";
  proposal_id: string;
  payload_hash: string;
  summary: string;
  zones: SetupZoneCard[];
  totals: { zones: number; tables: number; seats: number; new_zones: number; new_tables: number };
  duration_minutes: number | null;
  previous_duration_minutes: number | null;
  combination_off?: boolean;
}

export interface RepliesEvent {
  type: "replies";
  options: string[];
}

export interface AssistantProposal {
  type: "assistant_proposal";
  proposal_id: string;
  payload_hash: string;
  summary: string;
  assistant: { id: string; name: string; is_active: boolean };
  channels: Array<{ name: string; type: string }>;
  tools: Array<{ name: string; label: string }>;
  replaced_tools: string[];
  changes: Array<{ field: "reservation_method" | "business_type"; before: string; after: string }>;
  large_groups: { max_auto_party_size: number; previous: number | null; quote: string } | null;
  previous_method: string;
}

export interface AssistantMissingEvent {
  type: "assistant_missing";
}

export type SetupEvent = SetupProposal | RepliesEvent | BookingPageProposal | BookingLinkEvent | AssistantProposal | AssistantMissingEvent;

export type SetupNotice = "output_truncated" | "no_reply" | "model_timeout" | "unavailable" | "forbidden" | "rate_limited" | "attachment";

export interface SetupAttachment {
  path: string;
  name: string;
}

export interface SetupItem {
  id: string;
  role: "user" | "assistant";
  text: string;
  attachments?: SetupAttachment[];
  replies?: string[];
  offerAttach?: boolean;
  notice?: SetupNotice;
  proposalId?: string;
  bookingProposalId?: string;
  bookingLink?: { slug: string; businessName: string | null };
  assistantProposalId?: string;
  assistantMissing?: boolean;
}

export interface SetupState {
  items: SetupItem[];
  proposal: SetupProposal | null;
  bookingPage?: BookingPageProposal | null;
  assistantProposal?: AssistantProposal | null;
}

export interface TurnResponse {
  reply: string;
  events: SetupEvent[];
  notice: SetupNotice | null;
}

export interface ExistingLayoutSummary {
  zones: number;
  tables: number;
}

export const NOTICE_COPY: Record<SetupNotice, string> = {
  output_truncated: "Mi respuesta quedó incompleta. Vuelve a intentarlo.",
  no_reply: "No pude responder esta vez. Vuelve a intentarlo.",
  model_timeout: "Tardé demasiado en responder. Vuelve a intentarlo en un momento.",
  unavailable: "No pude conectarme. Revisa tu conexión y vuelve a intentarlo.",
  forbidden: "No tienes permiso para configurar reservas en este espacio de trabajo.",
  rate_limited: "Vas muy rápido. Espera un minuto y vuelve a intentarlo.",
  attachment: "No pude leer la imagen. Usa una foto o plano en JPG, PNG o WEBP de hasta 5 MB.",
};

export const CONFIRM_ERROR_COPY: Record<string, string> = {
  proposal_stale: "Esta propuesta ya fue reemplazada por una más reciente. Revisa la que ves ahora.",
  proposal_hash_mismatch: "La propuesta cambió desde que la viste. Pídeme que la muestre de nuevo.",
  layout_changed: "Tus zonas o mesas cambiaron mientras tanto. Pídeme que arme la propuesta de nuevo.",
  forbidden: "No tienes permiso para crear zonas y mesas en este espacio de trabajo.",
  invalid_proposal: "La propuesta tiene un dato que no es válido. Pídeme que la ajuste.",
  proposal_not_found: "No encontré esta propuesta. Pídeme que la arme de nuevo.",
};

export const confirmErrorCopy = (code: string | null | undefined): string =>
  CONFIRM_ERROR_COPY[code ?? ""] ?? "No se pudieron crear las zonas y mesas. Vuelve a intentarlo.";

let sequence = 0;
const nextId = () => `setup-${Date.now()}-${++sequence}`;

export function openingItem(existing: ExistingLayoutSummary): SetupItem {
  if (existing.tables > 0) {
    return {
      id: nextId(),
      role: "assistant",
      text: `Ya tienes ${plural(existing.zones, "zona", "zonas")} con ${plural(existing.tables, "mesa", "mesas")}. ¿Qué quieres agregar? Puedo sumar mesas a una zona que ya tienes o crear zonas nuevas; lo que ya existe no se toca. Si tienes un plano o una foto del local, adjúntala y la leo.`,
      offerAttach: true,
      replies: [SHARE_LINK_REQUEST],
    };
  }
  return {
    id: nextId(),
    role: "assistant",
    text: "Vamos a dejar listas tus reservas. ¿Cómo está dividido el local? Dime tus zonas (salón, terraza, segundo piso…) y cuántas mesas tiene cada una, con cuántas personas caben. Si tienes un plano o una foto del local, adjúntala y la leo.",
    offerAttach: true,
    replies: ["Tengo un solo salón"],
  };
}

export function startState(existing: ExistingLayoutSummary): SetupState {
  return { items: [openingItem(existing)], proposal: null };
}

export function addUserTurn(state: SetupState, text: string, attachments: SetupAttachment[] = []): SetupState {
  const item: SetupItem = { id: nextId(), role: "user", text, ...(attachments.length > 0 ? { attachments } : {}) };
  return { ...state, items: [...state.items, item] };
}

export function addNotice(state: SetupState, notice: SetupNotice): SetupState {
  return { ...state, items: [...state.items, { id: nextId(), role: "assistant", text: "", notice }] };
}

export function applyTurnResponse(state: SetupState, response: TurnResponse): SetupState {
  const events = Array.isArray(response.events) ? response.events : [];
  let proposal = state.proposal;
  let bookingPage = state.bookingPage ?? null;
  const item: SetupItem = { id: nextId(), role: "assistant", text: (response.reply ?? "").trim() };
  let assistantProposal = state.assistantProposal ?? null;
  for (const event of events) {
    if (event.type === "proposal") {
      proposal = event;
      item.proposalId = event.proposal_id;
    } else if (event.type === "replies") {
      item.replies = event.options;
    } else if (event.type === "booking_page") {
      bookingPage = event;
      item.bookingProposalId = event.proposal_id;
    } else if (event.type === "booking_link") {
      item.bookingLink = { slug: event.slug, businessName: event.business_name || null };
    } else if (event.type === "assistant_proposal") {
      assistantProposal = event;
      item.assistantProposalId = event.proposal_id;
    } else if (event.type === "assistant_missing") {
      item.assistantMissing = true;
    }
  }
  if (response.notice) item.notice = response.notice;
  const visible = item.text || item.notice || item.proposalId || item.bookingProposalId || item.bookingLink || item.assistantProposalId || item.assistantMissing || (item.replies && item.replies.length > 0);
  return { items: visible ? [...state.items, item] : state.items, proposal, bookingPage, assistantProposal };
}

export function attachmentCount(state: SetupState): number {
  return state.items.reduce((sum, item) => sum + (item.attachments?.length ?? 0), 0);
}

export function historyOf(items: SetupItem[]): Array<{ role: "user" | "assistant"; content: string; attachments?: string[] }> {
  return items
    .filter((item) => !item.notice)
    .map((item) => ({
      role: item.role,
      content: item.text,
      ...(item.role === "user" && item.attachments?.length ? { attachments: item.attachments.map((file) => file.path) } : {}),
    }))
    .filter((message) => message.content.trim() !== "" || (message.attachments?.length ?? 0) > 0);
}

export function buildTurnRequest(organizationId: string, sessionId: string, state: SetupState) {
  return { action: "turn", organizationId, sessionId, messages: historyOf(state.items) };
}

export function buildConfirmRequest(proposal: SetupProposal) {
  return { action: "confirm", proposal_id: proposal.proposal_id, payload_hash: proposal.payload_hash };
}

export function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

export function doneTitle(zonesCreated: number, tablesCreated: number): string {
  if (zonesCreated === 0) return `Agregué ${plural(tablesCreated, "mesa", "mesas")}.`;
  return `Creé ${plural(zonesCreated, "zona", "zonas")} y ${plural(tablesCreated, "mesa", "mesas")}.`;
}

export function formatDuration(minutes: number | null): string {
  if (minutes === null) return "Sin definir";
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest} minutos`;
  const hourText = plural(hours, "hora", "horas");
  return rest === 0 ? hourText : `${hourText} y ${rest} minutos`;
}

export function tableRange(tables: SetupTable[]): string {
  if (tables.length === 0) return "";
  if (tables.length === 1) return `Mesa ${tables[0].table_number}`;
  return `Mesas ${tables[0].table_number} a ${tables[tables.length - 1].table_number}`;
}

export function seatsCopy(tables: SetupTable[]): string {
  const sizes = [...new Set(tables.map((table) => table.capacity))].sort((a, b) => a - b);
  if (sizes.length === 0) return "";
  if (sizes.length === 1) return tables.length === 1 ? `hasta ${plural(sizes[0], "persona", "personas")}` : `${plural(sizes[0], "persona", "personas")} c/u`;
  return `de ${sizes[0]} a ${sizes[sizes.length - 1]} personas`;
}

export function summaryLine(totals: SetupProposal["totals"]): string {
  return [plural(totals.zones, "zona", "zonas"), plural(totals.tables, "mesa", "mesas"), plural(totals.seats, "persona", "personas")].join(" · ");
}

export interface ConfirmOutcome {
  ok: boolean;
  status: number;
  error: string | null;
  zonesCreated: number;
  tablesCreated: number;
  alreadyApplied: boolean;
}

export async function post(accessToken: string, body: unknown): Promise<{ status: number; data: Record<string, unknown> }> {
  const response = await fetch(`${EXTERNAL_FUNCTIONS_URL}/${SETUP_FUNCTION}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", apikey: EXTERNAL_SUPABASE_ANON_KEY, Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  return { status: response.status, data: data && typeof data === "object" ? data as Record<string, unknown> : {} };
}

const failed = (notice: SetupNotice): TurnResponse => ({ reply: "", events: [], notice });

export async function requestTurn(accessToken: string, body: unknown): Promise<TurnResponse> {
  try {
    const { status, data } = await post(accessToken, body);
    if (status === 504 || data.error === "model_timeout") return failed("model_timeout");
    if (status === 403) return failed("forbidden");
    if (status === 429) return failed("rate_limited");
    if (status === 422 && typeof data.error === "string" && data.error.startsWith("attachment_")) return failed("attachment");
    if (status !== 200) return failed("unavailable");
    return {
      reply: typeof data.reply === "string" ? data.reply : "",
      events: Array.isArray(data.events) ? data.events as SetupEvent[] : [],
      notice: (data.notice as SetupNotice | null) ?? null,
    };
  } catch {
    return failed("unavailable");
  }
}

export async function requestConfirm(accessToken: string, proposal: SetupProposal): Promise<ConfirmOutcome> {
  try {
    const { status, data } = await post(accessToken, buildConfirmRequest(proposal));
    return {
      ok: status === 200,
      status,
      error: status === 200 ? null : (typeof data.error === "string" ? data.error : "confirm_failed"),
      zonesCreated: Number(data.zones_created ?? 0),
      tablesCreated: Number(data.tables_created ?? 0),
      alreadyApplied: data.already_applied === true,
    };
  } catch {
    return { ok: false, status: 0, error: "confirm_failed", zonesCreated: 0, tablesCreated: 0, alreadyApplied: false };
  }
}

export const CONNECT_ASSISTANT_MESSAGE = "Quiero que mi asistente de WhatsApp también tome reservas.";

export const ASSISTANT_TOOL_COPY: Record<string, string> = {
  check_availability: "Consultar qué mesas hay libres",
  create_reservation: "Crear la reserva con una de tus mesas",
  update_reservation: "Corregir una reserva que ya creó",
  start_reservation_flow: "Pasar la conversación a tu flujo de reservas",
};

export const CHANNEL_TYPE_COPY: Record<string, string> = {
  whatsapp: "WhatsApp",
  instagram: "Instagram",
  facebook: "Messenger",
  messenger: "Messenger",
  web: "Chat web",
  sms: "SMS",
  email: "Correo",
};

const RESERVATION_METHOD_COPY: Record<string, string> = {
  system: "Con tus mesas",
  link: "Compartir un enlace",
  phone: "Dar un teléfono",
};

export function reservationMethodCopy(method: string): string {
  if (!method) return "Sin definir";
  return RESERVATION_METHOD_COPY[method] ?? method;
}

export function businessTypeCopy(type: string): string {
  if (!type) return "Sin definir";
  return type.charAt(0).toUpperCase() + type.slice(1);
}

export function assistantChangeCopy(change: AssistantProposal["changes"][number]): { label: string; before: string; after: string } {
  if (change.field === "reservation_method") {
    return { label: "Cómo toma reservas", before: reservationMethodCopy(change.before), after: reservationMethodCopy(change.after) };
  }
  return { label: "Tipo de negocio", before: businessTypeCopy(change.before), after: businessTypeCopy(change.after) };
}

export const ASSISTANT_CONFIRM_ERROR_COPY: Record<string, string> = {
  proposal_stale: "Esta propuesta ya fue reemplazada por una más reciente. Revisa la que ves ahora.",
  proposal_hash_mismatch: "La propuesta cambió desde que la viste. Pídeme que la muestre de nuevo.",
  assistant_changed: "Tu asistente cambió mientras tanto. Pídeme que arme la propuesta de nuevo.",
  assistant_not_found: "No encontré ese asistente. Pídeme que arme la propuesta de nuevo.",
  tools_managed_by_user: "Las herramientas de reservas de tu negocio se configuran a mano en Automatizaciones.",
  no_tables: "Primero crea tus zonas y mesas: el asistente reserva con ellas.",
  forbidden: "No tienes permiso para cambiar asistentes en este espacio de trabajo.",
  invalid_proposal: "La propuesta tiene un dato que no es válido. Pídeme que la ajuste.",
  proposal_not_found: "No encontré esta propuesta. Pídeme que la arme de nuevo.",
};

export const assistantConfirmErrorCopy = (code: string | null | undefined): string =>
  ASSISTANT_CONFIRM_ERROR_COPY[code ?? ""] ?? "No se pudieron activar las reservas en tu asistente. Vuelve a intentarlo.";

export function buildConfirmAssistantRequest(proposal: AssistantProposal) {
  return { action: "confirm_assistant", proposal_id: proposal.proposal_id, payload_hash: proposal.payload_hash };
}

export interface AssistantConfirmOutcome {
  ok: boolean;
  status: number;
  error: string | null;
  backupVersionNumber: number | null;
  alreadyApplied: boolean;
}

export async function requestConfirmAssistant(accessToken: string, proposal: AssistantProposal): Promise<AssistantConfirmOutcome> {
  try {
    const { status, data } = await post(accessToken, buildConfirmAssistantRequest(proposal));
    return {
      ok: status === 200,
      status,
      error: status === 200 ? null : (typeof data.error === "string" ? data.error : "confirm_failed"),
      backupVersionNumber: typeof data.backup_version_number === "number" ? data.backup_version_number : null,
      alreadyApplied: data.already_applied === true,
    };
  } catch {
    return { ok: false, status: 0, error: "confirm_failed", backupVersionNumber: null, alreadyApplied: false };
  }
}
